import {
  requestGet as wsRequestGet,
  requestPost as wsRequestPost,
  requestPatch as wsRequestPatch,
  requestDelete as wsRequestDelete,
} from './_wsApiFetchData';

const BASE = '/admin/PlayEvent';

export async function list(_args, language, timeZone, cancelToken) {
  return wsRequestGet(BASE, language, timeZone, null, cancelToken);
}

export async function get(eventId, language, timeZone, cancelToken) {
  return wsRequestGet(`${BASE}/${eventId}`, language, timeZone, null, cancelToken);
}

export async function getSession(sessionId, language, timeZone, cancelToken) {
  return wsRequestGet(`${BASE}/sessions/${sessionId}`, language, timeZone, null, cancelToken);
}

// Edit a session. body may include startsAtUtc, endsAtUtc, capacity,
// courtCount (updated when present) and signupDeadlineUtc (string|null —
// null clears the explicit deadline and reverts to default 24h before start).
export async function updateSession(sessionId, body, language, timeZone, cancelToken) {
  return wsRequestPatch(
    `${BASE}/sessions/${sessionId}`,
    language,
    timeZone,
    body,
    null,
    cancelToken,
  );
}

// Add a new session to an existing event.
// body: { startsAtUtc, endsAtUtc, capacity?, courtCount?, signupDeadlineUtc? }.
export async function addSession(eventId, body, language, timeZone, cancelToken) {
  return wsRequestPost(
    `${BASE}/${eventId}/sessions`,
    language,
    timeZone,
    body,
    null,
    cancelToken,
  );
}

// Delete a session. Backend refuses (409 `{ reason: "not_empty", hasRegistrations,
// hasWatchlist, hasPairings }`) if the session has *ever* had a signup (cancelled
// ones included), a waitlist entry or a pairing generation (inactive ones
// included) — so this only removes empty/mistaken sessions. The refusal is
// rethrown with a readable message, since the counts on the page cover only the
// current signups, waitlist and active pairings.
export async function deleteSession(sessionId, language, timeZone, cancelToken) {
  try {
    return await wsRequestDelete(
      `${BASE}/sessions/${sessionId}`,
      language,
      timeZone,
      null,
      null,
      cancelToken,
    );
  } catch (e) {
    const body = e?.payload?.responseData;
    if (e?.payload?.statusCode === 409 && body?.reason === 'not_empty') {
      const history = [
        body.hasRegistrations && 'signups (cancelled ones count too)',
        body.hasWatchlist && 'waitlist entries (removed ones count too)',
        body.hasPairings && 'pairings (earlier generations count too)',
      ].filter(Boolean);
      const error = new Error(`The session cannot be deleted: it has ${history.join(', ') || 'history'}. Only a session nobody ever signed up for can go.`);
      error.payload = e.payload;
      throw error;
    }
    throw e;
  }
}

export async function generatePairings(sessionId, body, language, timeZone, cancelToken) {
  return wsRequestPost(
    `${BASE}/sessions/${sessionId}/pairings/generate`,
    language,
    timeZone,
    body || {},
    null,
    cancelToken,
  );
}

export async function movePairing(pairingId, body, language, timeZone, cancelToken) {
  return wsRequestPatch(
    `${BASE}/pairings/${pairingId}`,
    language,
    timeZone,
    body,
    null,
    cancelToken,
  );
}

export async function sendPairingsEmail(sessionId, language, timeZone, cancelToken) {
  return wsRequestPost(
    `${BASE}/sessions/${sessionId}/send-pairings`,
    language,
    timeZone,
    {},
    null,
    cancelToken,
  );
}

// Render the pairings email for a specific registrant without sending it.
// Returns { toEmail, subject, html }. The admin UI opens the html in a new
// window so the admin can sanity-check translation, court assignment, and
// teammate names before pressing Send for everyone.
export async function previewPairing(sessionId, registrationId, language, timeZone, cancelToken) {
  return wsRequestGet(
    `${BASE}/sessions/${sessionId}/preview-pairings?registrationId=${encodeURIComponent(registrationId)}`,
    language,
    timeZone,
    null,
    cancelToken,
  );
}

export async function create(body, language, timeZone, cancelToken) {
  return wsRequestPost(BASE, language, timeZone, body, null, cancelToken);
}
